import { useState, useMemo, useEffect, Fragment } from "react";
import type { Dispatch, SetStateAction } from "react";
import AeroScene from "./scene";
import { ENM_DATA, CD_GROUPS, PLAYERS, CD_MS, kiKey, fmtGil, fmtGilShort, fmtDate } from "./data";
import type { ENM, Player, Drop, Sale, PriceEntry, KIState } from "./data";
import { useDrops, useSales, useKIState, usePrices, useIdentity, useRealtimeReady, useRealtimeConnected, useRealtimePresence, useRealtimeActions } from "./api";

// ─── AeroCSS — full Frutiger Aero stylesheet ──────────────────────────────────

const AeroCSS = `
@import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&family=Inter:wght@400;500;600;700&family=Orbitron:wght@500;700;900&display=swap');

*{box-sizing:border-box}
html,body{margin:0;padding:0}
body{font-family:'Quicksand',sans-serif;color:#eaf6ff;-webkit-font-smoothing:antialiased;overflow-x:hidden}

@keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
@keyframes orbFloat{0%,100%{transform:translateY(0) rotate(0deg)}50%{transform:translateY(-8px) rotate(0.6deg)}}
@keyframes drift{0%{transform:translateX(0) scale(var(--s,1))}100%{transform:translateX(120vw) scale(var(--s,1))}}
@keyframes bubbleRise{0%{transform:translateY(0) translateX(0);opacity:0}10%{opacity:var(--o,0.3)}90%{opacity:var(--o,0.3)}100%{transform:translateY(-110vh) translateX(40px);opacity:0}}
@keyframes swim0{0%{transform:translateX(0)}100%{transform:translateX(120vw)}}
@keyframes swim1{0%{transform:translateX(0)}100%{transform:translateX(-120vw)}}
@keyframes caustic{0%,100%{transform:translateX(-10%) translateY(0)}50%{transform:translateX(10%) translateY(-3%)}}
@keyframes iri{0%{filter:hue-rotate(0deg)}100%{filter:hue-rotate(360deg)}}
@keyframes wobble{0%,100%{transform:rotate(-1.5deg) translateY(0)}50%{transform:rotate(1.5deg) translateY(-4px)}}
@keyframes sheen{0%{transform:translateX(-100%) skewX(-22deg)}100%{transform:translateX(300%) skewX(-22deg)}}
@keyframes blip{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.4);opacity:0.6}}
@keyframes ringPulse{0%{transform:scale(1);opacity:0.8}100%{transform:scale(2.4);opacity:0}}
@keyframes ticker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
@keyframes barLoad{0%{background-position:0 0}100%{background-position:40px 0}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.65}}

/* ─── GLASS ORB ─── */
.orb{
  position:relative;border-radius:50%;
  background:radial-gradient(120% 120% at 35% 25%, rgba(255,255,255,0.95) 0%, rgba(220,250,255,0.6) 18%, rgba(140,225,255,0.3) 45%, rgba(70,170,210,0.35) 80%, rgba(20,90,130,0.55) 100%);
  border:1px solid rgba(255,255,255,0.7);
  box-shadow:
    0 1px 0 rgba(255,255,255,1) inset,
    0 -10px 24px rgba(20,80,120,0.5) inset,
    8px 12px 30px rgba(0,60,110,0.45),
    -2px -2px 14px rgba(180,240,255,0.18) inset,
    0 0 28px rgba(150,230,255,0.3);
  display:flex;flex-direction:column;align-items:center;justify-content:center;
  text-align:center;overflow:hidden;
}
.orb::before{
  content:'';position:absolute;left:18%;right:30%;top:8%;height:32%;
  border-radius:50% / 80%;
  background:linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.0) 100%);
  pointer-events:none;filter:blur(1px);
}
.orb::after{
  content:'';position:absolute;left:50%;bottom:8%;width:30%;height:8%;transform:translateX(-50%);
  border-radius:50%;
  background:radial-gradient(50% 50%, rgba(255,255,255,0.65), transparent);
  pointer-events:none;
}
.orb-mint{background:radial-gradient(120% 120% at 35% 25%, rgba(255,255,255,0.95) 0%, rgba(225,255,235,0.6) 16%, rgba(150,240,200,0.4) 42%, rgba(60,200,140,0.4) 78%, rgba(15,110,75,0.6) 100%)}
.orb-violet{background:radial-gradient(120% 120% at 35% 25%, rgba(255,255,255,0.95) 0%, rgba(240,225,255,0.6) 16%, rgba(200,170,255,0.42) 42%, rgba(140,100,230,0.4) 78%, rgba(70,30,140,0.6) 100%)}
.orb-amber{background:radial-gradient(120% 120% at 35% 25%, rgba(255,255,255,0.95) 0%, rgba(255,240,200,0.6) 16%, rgba(255,210,120,0.45) 42%, rgba(240,160,40,0.45) 78%, rgba(140,80,10,0.6) 100%)}
.orb-rose{background:radial-gradient(120% 120% at 35% 25%, rgba(255,255,255,0.95) 0%, rgba(255,225,235,0.6) 16%, rgba(255,170,200,0.45) 42%, rgba(240,100,150,0.45) 78%, rgba(140,30,75,0.6) 100%)}

.orb-num{font-family:'Quicksand',sans-serif;font-weight:700;font-size:42px;letter-spacing:-1.5px;line-height:0.9;color:#fff;text-shadow:0 2px 0 rgba(255,255,255,0.5), 0 4px 12px rgba(0,60,90,0.45), 0 0 20px rgba(255,255,255,0.4);transform:translateY(-0.04em)}
.orb-num-sm{font-size:28px}
.orb-cap{font-family:'Inter',sans-serif;font-size:9px;font-weight:700;letter-spacing:2.4px;color:rgba(255,255,255,0.85);text-transform:uppercase;text-shadow:0 1px 0 rgba(0,60,90,0.3);margin-top:4px}

/* ─── CHROME RIBBON ─── */
.chrome-ribbon{
  position:relative;border-radius:999px;padding:18px 32px;
  background:linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(220,240,250,0.6) 18%, rgba(140,180,220,0.5) 45%, rgba(80,140,190,0.55) 60%, rgba(180,220,245,0.7) 88%, rgba(255,255,255,0.85) 100%);
  border:1px solid rgba(255,255,255,0.85);
  box-shadow:
    0 1px 0 rgba(255,255,255,1) inset,
    0 -2px 6px rgba(40,90,140,0.4) inset,
    0 14px 36px rgba(20,60,110,0.32),
    0 4px 12px rgba(20,60,110,0.14);
  overflow:hidden;
}
.chrome-ribbon::before{
  content:'';position:absolute;inset:0;border-radius:inherit;pointer-events:none;
  background:linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 48%, rgba(255,255,255,0.0) 56%, rgba(180,220,250,0.4) 70%, transparent 90%);
  animation:sheen 8s ease-in-out infinite;
  mix-blend-mode:screen;
}

/* ─── GLASS CARD ─── */
.glass{
  position:relative;border-radius:32px;
  background:linear-gradient(180deg, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.1) 38%, rgba(180,235,255,0.06) 100%),
    linear-gradient(135deg, rgba(160,235,255,0.2), rgba(120,210,235,0.1));
  backdrop-filter:blur(28px) saturate(1.7);
  -webkit-backdrop-filter:blur(28px) saturate(1.7);
  border:1px solid rgba(255,255,255,0.55);
  box-shadow:
    0 1px 0 rgba(255,255,255,0.95) inset,
    0 -1px 0 rgba(120,200,230,0.3) inset,
    0 18px 44px rgba(0,60,110,0.25),
    0 4px 14px rgba(0,60,110,0.1);
  overflow:hidden;
}
.glass::before{
  content:'';position:absolute;inset:0;border-radius:inherit;pointer-events:none;
  background:linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.18) 22%, transparent 50%);
  height:50%;
}

/* ─── PILLS ─── */
.pill{
  font-family:'Quicksand',sans-serif;font-weight:700;font-size:13px;letter-spacing:0.2px;
  padding:10px 22px;border-radius:999px;border:1px solid rgba(255,255,255,0.6);
  cursor:pointer;position:relative;overflow:hidden;
  transition:transform .2s cubic-bezier(.33,1,.68,1), box-shadow .2s;
  text-shadow:0 1px 0 rgba(255,255,255,0.4);outline:none;
}
.pill::before{
  content:'';position:absolute;left:0;right:0;top:0;height:50%;border-radius:999px 999px 0 0;
  background:linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.0) 100%);
  pointer-events:none;
}
.pill:hover:not(:disabled){transform:translateY(-2px)}
.pill:active{transform:translateY(0)}
.pill:disabled{opacity:0.45;cursor:not-allowed}
.pill:focus-visible{outline:2px solid rgba(180,240,255,0.85);outline-offset:2px}

.pill-aqua{background:linear-gradient(180deg, #b3eaff 0%, #6ec5ee 50%, #2e9bd0 100%);color:#063a5a;box-shadow:0 1px 0 rgba(255,255,255,0.85) inset, 0 -2px 6px rgba(0,80,140,0.3) inset, 0 6px 14px rgba(0,100,170,0.3)}
.pill-mint{background:linear-gradient(180deg, #c5f7d8 0%, #7fe0b0 50%, #2bb878 100%);color:#0a4a30;box-shadow:0 1px 0 rgba(255,255,255,0.8) inset, 0 -2px 6px rgba(0,100,60,0.28) inset, 0 6px 14px rgba(0,140,90,0.28)}
.pill-coral{background:linear-gradient(180deg, #ffd3c8 0%, #ff998a 50%, #e85a48 100%);color:#5a1410;box-shadow:0 1px 0 rgba(255,255,255,0.75) inset, 0 -2px 6px rgba(160,40,30,0.28) inset, 0 6px 14px rgba(220,80,60,0.28)}
.pill-violet{background:linear-gradient(180deg, #e8d7ff 0%, #b89ce8 50%, #7a4ad0 100%);color:#3a1a78;box-shadow:0 1px 0 rgba(255,255,255,0.8) inset, 0 -2px 6px rgba(60,20,140,0.3) inset, 0 6px 14px rgba(140,80,220,0.3)}
.pill-ghost{background:linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(220,245,255,0.25) 50%, rgba(180,225,245,0.18) 100%);color:#0e4f74;box-shadow:0 1px 0 rgba(255,255,255,0.7) inset, 0 -1px 4px rgba(50,130,180,0.18) inset, 0 4px 12px rgba(0,80,120,0.1)}
.pill-on{box-shadow:0 1px 0 rgba(255,255,255,1) inset, 0 -2px 6px rgba(0,80,140,0.32) inset, 0 8px 20px rgba(0,140,210,0.45), 0 0 0 2px rgba(180,240,255,0.45)}
/* WCAG 2.5.8 (AA) minimum target size is 24×24 CSS px. inline-flex so the
   min height extends the button without shifting text alignment. */
.pill-sm{padding:6px 16px;font-size:11px;min-height:32px;display:inline-flex;align-items:center;justify-content:center}
.pill-xs{padding:3px 10px;font-size:10px;min-height:24px;min-width:24px;display:inline-flex;align-items:center;justify-content:center}

/* ─── TAGS ─── */
.tag{display:inline-flex;align-items:center;gap:6px;font-family:'Inter',sans-serif;font-size:10px;font-weight:700;letter-spacing:0.4px;padding:5px 13px;border-radius:999px;border:1px solid rgba(255,255,255,0.55);background:linear-gradient(180deg,rgba(255,255,255,0.5),rgba(255,255,255,0.15));color:#0c4a6e;text-shadow:0 1px 0 rgba(255,255,255,0.4);position:relative}
.tag::before{content:'';position:absolute;left:0;right:0;top:0;height:50%;border-radius:999px 999px 0 0;background:linear-gradient(180deg,rgba(255,255,255,0.6),transparent)}
.tag-mint{background:linear-gradient(180deg,#d6f7e3,#9be3bd);color:#0a4a30}
.tag-amber{background:linear-gradient(180deg,#ffe9b8,#ffc977);color:#6a3a08}
.tag-violet{background:linear-gradient(180deg,#e3d6ff,#b89ce8);color:#3a1a78}
.tag-slate{background:linear-gradient(180deg,#e2e8ee,#a9b6c2);color:#28323d}

/* ─── COOLDOWN TAG — text swaps on hover/focus ─── */
.tag-cd{cursor:help;outline:none;transition:padding .18s ease}
.tag-cd .cd-abs{display:none}
.tag-cd:hover .cd-rel,.tag-cd:focus-visible .cd-rel{display:none}
.tag-cd:hover .cd-abs,.tag-cd:focus-visible .cd-abs{display:inline}
.tag-cd:focus-visible{box-shadow:0 0 0 2px rgba(255,217,122,0.55)}

/* ─── HEADINGS ─── */
.h-aero{font-family:'Quicksand',sans-serif;font-weight:700;letter-spacing:-0.6px;color:#fff;text-shadow:0 1px 0 rgba(255,255,255,0.4), 0 2px 14px rgba(0,80,120,0.3), 0 0 30px rgba(180,240,255,0.18)}
.h-mega{font-family:'Quicksand',sans-serif;font-weight:700;letter-spacing:-2px;color:#fff;text-shadow:0 2px 0 rgba(255,255,255,0.5), 0 4px 18px rgba(0,80,120,0.4), 0 0 40px rgba(180,240,255,0.3)}
.h-script{font-family:'Quicksand',sans-serif;font-weight:300;font-style:italic;letter-spacing:-1px;color:rgba(255,255,255,0.92);text-shadow:0 1px 0 rgba(255,255,255,0.3), 0 2px 12px rgba(0,80,120,0.3)}
.hud{font-family:'Inter',sans-serif;font-size:9px;font-weight:600;letter-spacing:1.8px;color:rgba(255,255,255,0.62);text-transform:uppercase}
.hud-y2k{font-family:'Orbitron',sans-serif;font-size:9px;font-weight:500;letter-spacing:3px;color:rgba(255,255,255,0.7);text-transform:uppercase}

.price-mint{font-family:'Quicksand',sans-serif;font-weight:700;color:#caffd9;text-shadow:0 1px 0 rgba(0,90,60,0.35), 0 0 14px rgba(120,255,180,0.45)}
.price-aqua{font-family:'Quicksand',sans-serif;font-weight:700;color:#cbf2ff;text-shadow:0 1px 0 rgba(0,80,120,0.35), 0 0 14px rgba(120,220,255,0.45)}

/* ─── INPUTS ─── */
.input-aero{font-family:'Quicksand',sans-serif;font-size:13px;font-weight:600;padding:10px 16px;border:1px solid rgba(255,255,255,0.6);border-radius:999px;background:linear-gradient(180deg, rgba(255,255,255,0.7), rgba(220,245,255,0.4));color:#063a5a;outline:none;box-shadow:0 1px 0 rgba(255,255,255,0.85) inset, 0 -1px 2px rgba(60,140,180,0.15) inset, 0 2px 6px rgba(0,80,120,0.06);min-width:0}
.input-aero:focus{border-color:rgba(120,200,255,0.9);box-shadow:0 1px 0 rgba(255,255,255,1) inset, 0 0 0 3px rgba(120,200,255,0.25)}
select.input-aero{appearance:none;-webkit-appearance:none;padding-right:32px;background:linear-gradient(180deg, rgba(255,255,255,0.7), rgba(220,245,255,0.4)) padding-box, url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M0 0l5 6 5-6z' fill='%23063a5a'/></svg>") no-repeat right 14px center}

/* ─── SCROLL ─── */
.scroll-aero{max-height:540px;overflow-y:auto}
.scroll-aero::-webkit-scrollbar{width:6px}
.scroll-aero::-webkit-scrollbar-thumb{background:linear-gradient(180deg, rgba(180,240,255,0.6), rgba(80,180,220,0.4));border-radius:3px}

/* ─── KI STEPS ─── */
/* Concentric corner: inner radius = outer (player card 12) − padding (16, but
   we tighten ki-step's perceived gap via its own padding 12px). 6px keeps the
   row visibly curved while staying clearly smaller than its 12px parent. */
.ki-step{display:flex;align-items:center;gap:14px;padding:12px 16px;border-radius:6px;margin-bottom:6px;background:linear-gradient(180deg, rgba(255,255,255,0.18), rgba(180,235,255,0.06));border:1px solid rgba(255,255,255,0.3);transition:all .25s;cursor:pointer}
.ki-step.disabled{cursor:not-allowed;opacity:0.55}
.ki-step.done{background:linear-gradient(180deg, rgba(170,240,200,0.32), rgba(110,220,170,0.12));border-color:rgba(140,235,180,0.5)}
.ki-orb-num{width:34px;height:34px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px;line-height:1;background:radial-gradient(120% 100% at 30% 25%, #ffffff, #d8eef8 30%, #88b8d0 80%, #4a8aaa 100%);border:1px solid rgba(255,255,255,0.7);color:#063a5a;box-shadow:0 1px 0 rgba(255,255,255,0.9) inset, 0 -2px 6px rgba(20,80,110,0.35) inset, 0 4px 10px rgba(0,80,120,0.22)}
.ki-orb-num.done{background:radial-gradient(120% 100% at 30% 25%, #ffffff, #ccf5db 30%, #6fd8a4 70%, #2aa069 100%);color:#053a25;box-shadow:0 1px 0 rgba(255,255,255,0.9) inset, 0 -2px 6px rgba(15,90,55,0.4) inset, 0 4px 12px rgba(40,170,100,0.45), 0 0 0 2px rgba(170,240,200,0.45)}

/* ─── CHROME PUDDLE ─── */
.chrome-puddle{
  position:relative;
  background:radial-gradient(80% 60% at 50% 30%, #ffffff 0%, #d4e8f8 18%, #7eb0d8 50%, #3878a8 80%, #1a4a78 100%);
  border:1px solid rgba(255,255,255,0.85);
  box-shadow:
    0 1px 0 rgba(255,255,255,1) inset,
    0 -3px 8px rgba(20,60,100,0.5) inset,
    0 10px 24px rgba(0,80,140,0.45),
    0 0 32px rgba(150,220,255,0.4);
  overflow:hidden;
}
.chrome-puddle::before{
  content:'';position:absolute;left:14%;right:30%;top:8%;height:30%;
  border-radius:50% / 80%;
  background:linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0));
  pointer-events:none;
}

/* ─── POL TOP BAR ─── */
.pol-bar{
  position:relative;display:flex;align-items:center;gap:14px;padding:8px 18px;
  background:linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(220,240,250,0.55) 14%, rgba(140,180,220,0.45) 50%, rgba(80,140,190,0.5) 62%, rgba(180,220,245,0.62) 88%, rgba(255,255,255,0.78) 100%);
  border:1px solid rgba(255,255,255,0.85);
  border-radius:14px;
  box-shadow:0 1px 0 rgba(255,255,255,1) inset, 0 -2px 5px rgba(40,90,140,0.4) inset, 0 8px 20px rgba(20,60,110,0.22);
  font-family:'Inter',sans-serif;font-size:11px;font-weight:700;letter-spacing:1.2px;
  color:#0a3a5a;text-shadow:0 1px 0 rgba(255,255,255,0.7);text-transform:uppercase;
  overflow:hidden;
}
.pol-bar::before{
  content:'';position:absolute;left:0;right:0;top:0;height:50%;border-radius:14px 14px 0 0;pointer-events:none;
  background:linear-gradient(180deg,rgba(255,255,255,0.55),transparent);
}

/* ─── BLIP ─── */
.blip{position:relative;width:9px;height:9px;border-radius:50%;background:radial-gradient(circle at 35% 30%, #d4ffe6, #5ee0a0 50%, #1a8a55);box-shadow:0 0 8px #6ef0a8, 0 0 0 1px rgba(255,255,255,0.7) inset;animation:blip 2s ease-in-out infinite;flex-shrink:0}
.blip::after{content:'';position:absolute;inset:-3px;border-radius:50%;border:1.5px solid rgba(110,240,168,0.7);animation:ringPulse 2s ease-out infinite}
.blip-amber{background:radial-gradient(circle at 35% 30%, #fff0c4, #ffc864 50%, #aa6500);box-shadow:0 0 8px #ffd97a, 0 0 0 1px rgba(255,255,255,0.7) inset}
.blip-amber::after{border-color:rgba(255,217,122,0.7)}
.blip-violet{background:radial-gradient(circle at 35% 30%, #f0d9ff, #c89cf5 50%, #6428aa);box-shadow:0 0 8px #d4a8ff, 0 0 0 1px rgba(255,255,255,0.7) inset}
.blip-violet::after{border-color:rgba(212,168,255,0.7)}
.blip-rose{background:radial-gradient(circle at 35% 30%, #ffd9e0, #ff8aa0 50%, #aa1a3a);box-shadow:0 0 8px #ffa0b8, 0 0 0 1px rgba(255,255,255,0.7) inset}
.blip-rose::after{border-color:rgba(255,160,184,0.7)}

/* ─── TICKER ─── */
.ticker-wrap{flex:1;overflow:hidden;mask-image:linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent);-webkit-mask-image:linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent);min-width:0}
.ticker{display:inline-flex;gap:38px;white-space:nowrap;animation:ticker 60s linear infinite;font-family:'Inter',sans-serif;font-size:10px;font-weight:600;letter-spacing:1.4px;color:#0a3a5a;text-shadow:0 1px 0 rgba(255,255,255,0.7)}
.ticker span::before{content:'◇  ';color:#0e6dad}

/* ─── POL PANEL ─── */
.pol-panel{
  position:relative;border-radius:18px;
  background:linear-gradient(180deg, rgba(255,255,255,0.32) 0%, rgba(220,245,255,0.12) 38%, rgba(160,225,250,0.08) 100%);
  backdrop-filter:blur(20px) saturate(1.6);
  -webkit-backdrop-filter:blur(20px) saturate(1.6);
  border:1px solid rgba(255,255,255,0.5);
  box-shadow:0 1px 0 rgba(255,255,255,0.95) inset, 0 -1px 0 rgba(120,200,230,0.3) inset, 0 12px 32px rgba(0,60,110,0.22);
  overflow:hidden;
}
.pol-panel::before{
  content:'';position:absolute;left:0;right:0;top:0;height:32px;border-radius:18px 18px 0 0;pointer-events:none;
  background:linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.0) 100%);
}
.pol-panel-head{
  position:relative;padding:10px 16px;
  background:linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(190,220,240,0.4) 50%, rgba(120,180,215,0.45) 100%);
  border-bottom:1px solid rgba(255,255,255,0.55);
  font-family:'Inter',sans-serif;font-size:10px;font-weight:700;letter-spacing:2.4px;color:#0a3a5a;text-shadow:0 1px 0 rgba(255,255,255,0.65);text-transform:uppercase;
  display:flex;align-items:center;gap:10px;
}

/* ─── SCANLINES ─── */
.scanlines{position:relative}
.scanlines::after{
  content:'';position:absolute;inset:0;pointer-events:none;border-radius:inherit;
  background-image:repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px);
  mix-blend-mode:overlay;opacity:0.6;
}

/* ─── POL IDX (Orbitron leading-zero numerals) ─── */
.pol-idx{font-family:'Orbitron',sans-serif;font-size:10px;font-weight:700;letter-spacing:1.6px;color:rgba(255,255,255,0.65);text-shadow:0 1px 0 rgba(0,60,90,0.2)}

/* ─── ELBOW (chrome divider) ─── */
.elbow{position:relative;flex:1;height:14px;align-self:flex-end;margin-bottom:-2px}
.elbow::before{content:'';position:absolute;left:0;right:14px;bottom:0;height:1px;background:linear-gradient(90deg, rgba(255,255,255,0.5), rgba(255,255,255,0.15) 70%, transparent)}
.elbow::after{content:'';position:absolute;right:0;bottom:0;width:14px;height:14px;border-right:1px solid rgba(255,255,255,0.4);border-bottom:1px solid rgba(255,255,255,0.4);border-radius:0 0 6px 0;box-shadow:1px 1px 0 rgba(255,255,255,0.15) inset}

/* ─── CHROME PROGRESS BAR ─── */
.pol-bar-track{position:relative;height:5px;border-radius:3px;background:linear-gradient(180deg,rgba(0,40,70,0.35),rgba(0,60,100,0.18));border:1px solid rgba(255,255,255,0.2);overflow:hidden;box-shadow:0 1px 1px rgba(0,40,70,0.3) inset}
.pol-bar-fill{position:absolute;inset:0;border-radius:3px;background:linear-gradient(180deg, #d4ffe6 0%, #6ee0a8 50%, #2aa069 100%);box-shadow:0 1px 0 rgba(255,255,255,0.7) inset, 0 -1px 0 rgba(15,90,55,0.5) inset, 0 0 6px rgba(110,224,168,0.5);transform-origin:left}
.pol-bar-fill::after{content:'';position:absolute;inset:0;background-image:repeating-linear-gradient(45deg, rgba(255,255,255,0.2) 0 4px, transparent 4px 8px);animation:barLoad 1.4s linear infinite}
.pol-bar-fill-amber{background:linear-gradient(180deg, #ffe9b8 0%, #ffc864 50%, #d99500 100%);box-shadow:0 1px 0 rgba(255,255,255,0.7) inset, 0 -1px 0 rgba(140,80,0,0.5) inset, 0 0 6px rgba(255,200,100,0.5)}
.pol-bar-fill-rose{background:linear-gradient(180deg, #ffd6e0 0%, #ff8aa0 50%, #d9285a 100%);box-shadow:0 1px 0 rgba(255,255,255,0.7) inset, 0 -1px 0 rgba(140,30,60,0.5) inset, 0 0 6px rgba(255,140,160,0.5)}

/* ─── POL ROW ─── */
.pol-row{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:14px;padding:11px 18px;border-bottom:1px solid rgba(255,255,255,0.1);transition:background .2s}
.pol-row:hover{background:linear-gradient(90deg,rgba(255,255,255,0.08),rgba(180,235,255,0.05))}
.pol-row:last-child{border-bottom:none}
.pol-icon-chrome{
  width:28px;height:28px;border-radius:7px;flex-shrink:0;
  background:radial-gradient(120% 100% at 30% 25%, #ffffff, #d8eef8 25%, #88b8d0 75%, #4a8aaa 100%);
  border:1px solid rgba(255,255,255,0.7);
  box-shadow:0 1px 0 rgba(255,255,255,0.9) inset, 0 -2px 5px rgba(20,80,110,0.35) inset, 0 3px 8px rgba(0,80,120,0.2);
  display:flex;align-items:center;justify-content:center;line-height:1;
  font-family:'Orbitron',sans-serif;font-size:10px;font-weight:700;color:#063a5a;text-shadow:0 1px 0 rgba(255,255,255,0.6);
}
.pol-icon-chrome.amber{background:radial-gradient(120% 100% at 30% 25%, #ffffff, #ffe9c0 25%, #d8a05e 75%, #8a5a1e 100%)}

/* ─── POL FRIEND ─── */
/* Concentric with .pol-panel (18px) via 10px rail-content padding → 18−10 = 8 */
.pol-friend{
  display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:8px;cursor:pointer;
  background:linear-gradient(180deg, rgba(255,255,255,0.28), rgba(180,235,255,0.05));
  border:1px solid rgba(255,255,255,0.4);
  box-shadow:0 1px 0 rgba(255,255,255,0.7) inset, 0 4px 10px rgba(0,60,110,0.12);
  transition:all .2s;
}
.pol-friend.on{
  background:linear-gradient(180deg, rgba(180,225,250,0.55), rgba(80,160,210,0.35));
  box-shadow:0 1px 0 rgba(255,255,255,0.8) inset, 0 -1px 4px rgba(0,80,140,0.3) inset, 0 6px 14px rgba(0,140,210,0.32), 0 0 0 1.5px rgba(180,240,255,0.5);
}
.pol-friend-icon{
  width:32px;height:32px;border-radius:50%;flex-shrink:0;
  background:radial-gradient(120% 100% at 30% 25%, #ffffff, #d8eef8 25%, #88b8d0 75%, #4a8aaa 100%);
  border:1px solid rgba(255,255,255,0.7);
  display:flex;align-items:center;justify-content:center;line-height:1;
  font-family:'Orbitron',sans-serif;font-weight:700;font-size:14px;color:#063a5a;text-shadow:0 1px 0 rgba(255,255,255,0.6);
  box-shadow:0 1px 0 rgba(255,255,255,0.9) inset, 0 -2px 5px rgba(20,80,110,0.35) inset;
}

/* ─── POL NAV ─── */
/* Concentric with .pol-panel (18px) via 10px rail-content padding → 18−10 = 8 */
.pol-nav-item{
  display:flex;align-items:center;gap:10px;padding:10px 16px;border-radius:8px;cursor:pointer;font-family:'Quicksand',sans-serif;font-weight:700;font-size:14px;
  color:rgba(255,255,255,0.85);text-shadow:0 1px 0 rgba(0,60,90,0.3);
  border:1px solid transparent;transition:all .2s;
}
.pol-nav-item:hover{background:rgba(255,255,255,0.08)}
.pol-nav-item:focus-visible{outline:2px solid rgba(180,240,255,0.85);outline-offset:2px}
.pol-friend:focus-visible{outline:2px solid rgba(180,240,255,0.85);outline-offset:2px}
.pol-nav-item.on{
  background:linear-gradient(180deg, rgba(180,225,250,0.45), rgba(60,140,200,0.32));
  border-color:rgba(255,255,255,0.55);
  color:#fff;
  box-shadow:0 1px 0 rgba(255,255,255,0.65) inset, 0 -1px 3px rgba(0,80,140,0.3) inset, 0 4px 12px rgba(0,140,210,0.32);
}
.pol-nav-num{font-family:'Orbitron',sans-serif;font-size:10px;font-weight:700;letter-spacing:1.4px;opacity:0.75}

@media (max-width:680px){
  .detail-grid{grid-template-columns:1fr !important}
  .detail-rail{position:static !important}
  .header-orbs{display:none}
  .h-mega-title{font-size:60px !important}
  .h-mega-script{font-size:36px !important}
  .constellation{display:none}
}

@media (prefers-reduced-motion:reduce){
  .ticker, .blip, .blip::after, .orb, .chrome-ribbon::before{animation:none !important}
}
`;

// ─── Cooldown helpers ────────────────────────────────────────────────────────
//
// All KI lookups key on the cooldown-group key (enm.cdGroup / kiKey(enm)),
// never the ENM id — grouped ENMs (Holy Cow / Brothers) share one state row.

type GetKS = (kiKeyStr: string, p: string) => KIState;

function playerCdEnd(getKS: GetKS, kiKeyStr: string, p: string) {
  const ks = getKS(kiKeyStr, p);
  return ks.acquiredAt ? new Date(new Date(ks.acquiredAt).getTime() + CD_MS) : null;
}

function playerCdExpired(getKS: GetKS, kiKeyStr: string, p: string) {
  const e = playerCdEnd(getKS, kiKeyStr, p);
  return !e || e <= new Date();
}

function playerTimeLeft(getKS: GetKS, kiKeyStr: string, p: string) {
  const e = playerCdEnd(getKS, kiKeyStr, p);
  if (!e) return null;
  const diff = e.getTime() - Date.now();
  if (diff <= 0) return null;
  return `${Math.floor(diff / 864e5)}d ${Math.floor((diff % 864e5) / 36e5)}h`;
}

function CooldownTag({ prefix, end }: { prefix?: string; end: Date | null }) {
  if (!end) return null;
  const ms = end.getTime() - Date.now();
  if (ms <= 0) return null;
  const rel = `${Math.floor(ms / 864e5)}d ${Math.floor((ms % 864e5) / 36e5)}h`;
  const abs = end.toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
  const pre = prefix ? `${prefix} · ` : "";
  return (
    <span className="tag tag-amber tag-cd" tabIndex={0}>
      <span className="cd-rel">{pre}⏳ {rel}</span>
      <span className="cd-abs">{pre}{abs}</span>
    </span>
  );
}

function hasKI(getKS: GetKS, enm: ENM, p: string) {
  const ks = getKS(kiKey(enm), p);
  return enm.kiSteps.every(s => ks.steps[s.id]) && ks.needsRun;
}

function kiLabelFor(getKS: GetKS, enm: ENM): string | null {
  const j = hasKI(getKS, enm, "Jezza");
  const d = hasKI(getKS, enm, "Donpeyote");
  if (j && d) return "Both KI Ready";
  if (j) return "Jezza KI Ready";
  if (d) return "Donpeyote KI Ready";
  return null;
}

// Pre-farm progress badge for stone-trade ENMs (Test Your Mite, Bionic Bug).
// Surfaces the in-between state where the stone is in hand but the KI hasn't
// been traded for yet — so at a glance you know whether the next session
// starts at the NPC or back at the farm. Cooldown state is intentionally
// not consulted: pre-farming the next stone *during* the current cooldown
// is exactly the use case this badge exists for.
function stoneLabelFor(getKS: GetKS, enm: ENM): string | null {
  if (!enm.kiSteps.some(s => s.id === "stone")) return null;
  const lastId = enm.kiSteps[enm.kiSteps.length - 1].id;
  const farmed = (p: string) => {
    const ks = getKS(kiKey(enm), p);
    return !!ks.steps.stone && !ks.steps[lastId];
  };
  const j = farmed("Jezza");
  const d = farmed("Donpeyote");
  if (j && d) return "Both Stones Farmed";
  if (j) return "Jezza Stone Farmed";
  if (d) return "Donpeyote Stone Farmed";
  return null;
}

function ago(dateIso: string) {
  const ms = Date.now() - new Date(dateIso).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 1) return "JUST NOW";
  if (h < 24) return `${h}H AGO`;
  return `${Math.floor(h / 24)}D AGO`;
}

// ─── PRESENCE BANNER ─────────────────────────────────────────────────────────

export function PresenceBanner({ self }: { self: Player }) {
  const presence = useRealtimePresence();
  const others = presence.filter(p => p.player !== self);
  if (others.length === 0) return null;

  return (
    <div style={{
      padding: "8px 12px",
      background: "rgba(120, 180, 255, 0.12)",
      border: "1px solid rgba(120, 180, 255, 0.3)",
      borderRadius: 6,
      fontSize: 13,
      margin: "8px 0",
      animation: "pulse 2s ease-in-out infinite",
    }}>
      {others.map(p => {
        const enm = ENM_DATA.find(e => e.id === p.enm_id);
        const what = p.kind === "drop" ? "logging a run" : "logging a sale";
        return (
          <div key={`${p.player}-${p.enm_id}`}>
            <strong>{p.player}</strong> is {what} for <em>{enm?.name ?? p.enm_id}</em>…
          </div>
        );
      })}
    </div>
  );
}

// ─── APP ─────────────────────────────────────────────────────────────────────

export default function App() {
  const [view, setView] = useState<"dash" | "detail" | "sales">("dash");
  const [selId, setSelId] = useState<string | null>(null);
  const [tab, setTab] = useState<"drops" | "sales" | "ki" | "prices">("drops");
  const [logRunOpen, setLogRunOpen] = useState(false);

  const { player, setPlayer } = useIdentity();
  // Open the realtime WS once at the App root. All other realtime consumers
  // (PresenceBanner, POLTopBar, useDrops, useSales, useKIState) just
  // subscribe to the singleton without ensuring connection themselves.
  useRealtimeReady(player);

  // Once-a-minute re-render tick. Countdown tags, the READY badge, and the
  // ticker all derive from Date.now() at render time — without this, a tab
  // left open shows frozen "3d 4h" timers and holds READY/expired states
  // stale indefinitely.
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNowTick(n => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  const { drops, loaded: ld1, logRun, deleteDrop } = useDrops(player);
  const { sales, loaded: ld2, logSale, deleteSale, syncFromAH, syncing, syncError, syncResult } = useSales(player);
  const { kiState: _ki, loaded: ld3, getKS, toggleStep, spentKI, refresh: refreshKI } = useKIState();
  const { prices, loaded: ld4, updatePrice, refreshFromAH, refreshing, refreshError, refreshResults } = usePrices();
  const { sendPresence, clearPresence } = useRealtimeActions();
  const loaded = ld1 && ld2 && ld3 && ld4;

  // Single source of truth for what we want to broadcast as our open dialog.
  // Both LogRun (App-level dialog) and LogSale (SalesTab-local dialog) write
  // into this — having one bus instead of competing per-effect calls means
  // tab switches and dialog stacking no longer race to clearPresence over
  // each other. `null` = no dialog visible to the peer.
  const [presenceIntent, setPresenceIntent] = useState<PresenceIntent | null>(null);

  const totalRev = useMemo(() => sales.reduce((s, x) => s + (x.price || 0), 0), [sales]);
  const enmRev = (id: string) => sales.filter(s => s.enm_id === id).reduce((s, x) => s + (x.price || 0), 0);
  const enmRuns = (id: string) => drops.filter(d => d.enm_id === id).length;

  const sel = ENM_DATA.find(e => e.id === selId);

  const open = (e: ENM) => { setSelId(e.id); setView("detail"); setTab("drops"); };
  const home = () => { setView("dash"); };

  // Drive presenceIntent from the App-owned Log Run dialog state. Only
  // touches intent when this dialog opens or closes — never overwrites a
  // sale-kind intent set by SalesTab.
  useEffect(() => {
    if (logRunOpen && selId) {
      setPresenceIntent({ kind: "drop", enm_id: selId });
    } else {
      setPresenceIntent(prev => prev?.kind === "drop" ? null : prev);
    }
  }, [logRunOpen, selId]);

  // The single broadcaster — every change to presenceIntent emits exactly
  // one socket frame. Cleanup on App unmount fires clearPresence so a hot
  // reload or navigation doesn't leave stale presence on the peer.
  useEffect(() => {
    if (presenceIntent) sendPresence(presenceIntent.kind, presenceIntent.enm_id);
    else clearPresence();
    return () => clearPresence();
  }, [presenceIntent, sendPresence, clearPresence]);

  if (!loaded) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <style>{AeroCSS}</style>
        <AeroScene />
        <div className="pol-panel scanlines" style={{ padding: "32px 44px", zIndex: 2, position: "relative" }}>
          <div className="orb orb-mint" style={{ width: 72, height: 72, margin: "0 auto 14px", animation: "orbFloat 5s ease-in-out infinite" }}>
            <div className="orb-num" style={{ fontSize: 18 }}>···</div>
          </div>
          <div className="hud-y2k" style={{ textAlign: "center" }}>BOOTING · POL · ENM</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", position: "relative" }}>
      <style>{AeroCSS}</style>
      <AeroScene />

      <div style={{ position: "relative", zIndex: 2, maxWidth: 920, margin: "0 auto", padding: "26px 18px 120px" }}>
        <AmbientOrbs />
        <POLTopBar player={player} sales={sales} drops={drops} getKS={getKS} tick={nowTick} />
        <Header
          player={player}
          setPlayer={setPlayer}
          totalRev={totalRev}
          onLogo={home}
          onTotal={() => setView("sales")}
        />

        <PresenceBanner self={player} />

        {view === "dash" && (
          <Dashboard
            getKS={getKS}
            enmRev={enmRev}
            enmRuns={enmRuns}
            onOpen={open}
          />
        )}

        {view === "detail" && sel && (
          <DetailView
            enm={sel}
            drops={drops}
            sales={sales}
            prices={prices}
            getKS={getKS}
            tab={tab}
            setTab={setTab}
            player={player}
            setPresenceIntent={setPresenceIntent}
            logRunOpen={logRunOpen}
            setLogRunOpen={setLogRunOpen}
            onBack={home}
            rev={enmRev(sel.id)}
            runs={enmRuns(sel.id)}
            logRun={logRun}
            deleteDrop={deleteDrop}
            logSale={logSale}
            deleteSale={deleteSale}
            toggleStep={toggleStep}
            spentKI={spentKI}
            refreshKI={refreshKI}
            updatePrice={updatePrice}
            refreshFromAH={refreshFromAH}
            refreshing={refreshing}
            refreshError={refreshError}
            refreshResults={refreshResults}
          />
        )}

        {view === "sales" && (
          <AllSales
            sales={sales}
            totalRev={totalRev}
            onBack={home}
            deleteSale={deleteSale}
            syncFromAH={syncFromAH}
            syncing={syncing}
            syncError={syncError}
            syncResult={syncResult}
          />
        )}
      </div>
    </div>
  );
}

// ─── Ambient orbs — decorative scattered glass apples ─────────────────────────

function AmbientOrbs() {
  const orbs = [
    { top: "8%",  left: "-2%",  size: 28, color: "rose",   dur: 7,   delay: 0 },
    { top: "32%", left: "-3%",  size: 18, color: "amber",  dur: 5.5, delay: 1.2 },
    { top: "58%", left: "-1%",  size: 36, color: "mint",   dur: 8,   delay: 0.4 },
    { top: "78%", left: "-2%",  size: 22, color: "violet", dur: 6,   delay: 1.8 },
    { top: "14%", right: "-2%", size: 24, color: "violet", dur: 6.5, delay: 0.6 },
    { top: "44%", right: "-3%", size: 32, color: "aqua",   dur: 7.5, delay: 1.4 },
    { top: "68%", right: "-1%", size: 20, color: "amber",  dur: 5,   delay: 0.2 },
    { top: "90%", right: "-2%", size: 28, color: "rose",   dur: 6,   delay: 1.0 },
  ];
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1 }}>
      {orbs.map((o, i) => (
        <div key={i} className={`orb orb-${o.color === "aqua" ? "" : o.color}`} style={{
          position: "absolute",
          top: o.top, left: o.left, right: o.right,
          width: o.size, height: o.size,
          animation: `orbFloat ${o.dur}s ease-in-out infinite`,
          animationDelay: `${o.delay}s`,
          opacity: 0.85,
        }} />
      ))}
    </div>
  );
}

// ─── POL Top Bar ─────────────────────────────────────────────────────────────

function POLTopBar({ player, sales, drops, getKS, tick }: { player: Player; sales: Sale[]; drops: Drop[]; getKS: GetKS; tick: number }) {
  const connected = useRealtimeConnected();
  const news = useMemo(() => {
    const items: string[] = [];

    // Ready groups — both players cooldown-expired AND no holding KI run
    // pending. Grouped ENMs surface as one entry ("HOLY COW / BROTHERS"):
    // one KI, pick either fight.
    const ready = CD_GROUPS.filter(g =>
      PLAYERS.every(p => playerCdExpired(getKS, g.key, p) && !getKS(g.key, p).needsRun)
    );
    if (ready.length) {
      items.push(`READY · ${ready.map(g => g.label.toUpperCase()).join(" · ")}`);
    }

    // Soon-ready — any player whose cooldown ends within 24h
    const soon: string[] = [];
    for (const g of CD_GROUPS) {
      const slots: string[] = [];
      for (const p of PLAYERS) {
        const end = playerCdEnd(getKS, g.key, p);
        if (!end) continue;
        const ms = end.getTime() - Date.now();
        if (ms > 0 && ms <= 24 * 3600 * 1000) {
          const h = Math.max(1, Math.round(ms / 3600000));
          slots.push(`${p[0]} ${h}H`);
        }
      }
      if (slots.length) soon.push(`${g.label.toUpperCase()} · ${slots.join(" · ")}`);
    }
    if (soon.length) items.push(`SOON · ${soon.join("  ◇  ")}`);

    // Players with KI ready to spend — actionable signal. Named by the KI
    // (one Zephyr Fan, not one entry per fight it could open).
    const holding: string[] = [];
    for (const g of CD_GROUPS) {
      for (const p of PLAYERS) {
        if (hasKI(getKS, g.enms[0], p)) holding.push(`${p.toUpperCase()} · ${g.kiName.toUpperCase()}`);
      }
    }
    if (holding.length) items.push(`HOLDING KI · ${holding.join(" · ")}`);

    // Recent sales — top 3
    const recent = [...sales]
      .sort((a, b) => new Date(b.sale_date).getTime() - new Date(a.sale_date).getTime())
      .slice(0, 3);
    for (const s of recent) {
      items.push(`SALE · ${s.item_name.toUpperCase()} · ${fmtGilShort(s.price)} · ${ago(s.sale_date)}`);
    }

    // Last run logged (if any)
    const lastDrop = [...drops].sort((a, b) => new Date(b.run_date).getTime() - new Date(a.run_date).getTime())[0];
    const lastDropEnm = lastDrop ? ENM_DATA.find(e => e.id === lastDrop.enm_id) : null;
    if (lastDrop && lastDropEnm) {
      items.push(`LAST RUN · ${lastDropEnm.name.toUpperCase()} · ${ago(lastDrop.run_date)}`);
    }

    // Pool total — keep this one summary line
    const totalGil = sales.reduce((sum, x) => sum + (x.price || 0), 0);
    if (totalGil > 0) {
      items.push(`POOL · ${fmtGilShort(totalGil)} GIL · ${sales.length} RECEIPTS`);
    }

    return items.length ? items : ["NO ACTIVITY YET · LOG A RUN OR SYNC FROM AH"];
  }, [sales, drops, getKS, tick]);

  return (
    <div className="pol-bar" style={{ marginBottom: 14, animation: "fadeUp .4s ease both" }}>
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          className={`blip ${connected ? "" : "blip-amber"}`}
          title={connected ? "Realtime connected" : "Realtime reconnecting…"}
        />
        POL · ENM
      </span>
      <span style={{ opacity: 0.55 }}>│</span>
      <span style={{ fontSize: 10, opacity: 0.85, whiteSpace: "nowrap" }}>MEMBER · {player.toUpperCase()}</span>
      <span style={{ opacity: 0.55 }}>│</span>
      <div className="ticker-wrap">
        <div className="ticker">
          {[...news, ...news].map((n, i) => <span key={i}>{n}</span>)}
        </div>
      </div>
      <span style={{ opacity: 0.55 }}>│</span>
      <span style={{ fontSize: 10, opacity: 0.85, whiteSpace: "nowrap" }}>
        {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase()}
      </span>
    </div>
  );
}

// ─── Header — chrome ribbon, mega title, player switcher ─────────────────────

function Header({ player, setPlayer, totalRev, onLogo, onTotal }: {
  player: Player;
  setPlayer: (p: Player) => void;
  totalRev: number;
  onLogo: () => void;
  onTotal: () => void;
}) {
  const tint = player === "Jezza"
    ? "radial-gradient(closest-side, rgba(110,224,168,0.18), rgba(110,224,168,0) 70%)"
    : "radial-gradient(closest-side, rgba(212,168,255,0.18), rgba(212,168,255,0) 70%)";

  return (
    <div style={{ position: "relative", marginBottom: 36, animation: "fadeUp .5s ease both" }}>
      {/* Player tint */}
      <div aria-hidden="true" style={{
        position: "absolute", left: -120, right: -120, top: -60, height: 320,
        background: tint, pointerEvents: "none", filter: "blur(20px)", zIndex: 0,
        transition: "background .6s ease",
      }} />

      {/* Iridescent atmosphere */}
      <div aria-hidden="true" className="header-orbs" style={{ position: "absolute", inset: -30, pointerEvents: "none", overflow: "hidden", zIndex: 0 }}>
        <div style={{ position: "absolute", top: -20, left: "8%", width: 380, height: 260,
          background: "radial-gradient(closest-side, rgba(255,210,232,0.55), rgba(255,210,232,0) 70%)",
          filter: "blur(10px)", animation: "orbFloat 11s ease-in-out infinite" }} />
        <div style={{ position: "absolute", top: 30, left: "38%", width: 420, height: 280,
          background: "radial-gradient(closest-side, rgba(200,255,220,0.45), rgba(200,255,220,0) 72%)",
          filter: "blur(12px)", animation: "orbFloat 13s ease-in-out infinite", animationDelay: "1.4s" }} />
        <div style={{ position: "absolute", top: 10, right: "5%", width: 360, height: 240,
          background: "radial-gradient(closest-side, rgba(200,225,255,0.5), rgba(200,225,255,0) 70%)",
          filter: "blur(10px)", animation: "orbFloat 9s ease-in-out infinite", animationDelay: "0.7s" }} />
        <div style={{ position: "absolute", top: 60, left: "62%", width: 220, height: 180,
          background: "radial-gradient(closest-side, rgba(232,210,255,0.42), rgba(232,210,255,0) 70%)",
          filter: "blur(10px)", animation: "orbFloat 14s ease-in-out infinite", animationDelay: "2.1s" }} />
      </div>

      {/* Decorative apple-orbs floating beside the title */}
      <div className="orb orb-rose header-orbs"  style={{ position: "absolute", top: -10, right: 18, width: 56, height: 56, animation: "orbFloat 5s ease-in-out infinite", zIndex: 1 }} />
      <div className="orb orb-mint header-orbs"  style={{ position: "absolute", top: 64, right: 86, width: 32, height: 32, animation: "orbFloat 6s ease-in-out infinite", animationDelay: "0.7s", zIndex: 1 }} />
      <div className="orb orb-amber header-orbs" style={{ position: "absolute", top: 38, right: -8, width: 22, height: 22, animation: "orbFloat 4.5s ease-in-out infinite", animationDelay: "1.3s", zIndex: 1 }} />

      {/* Title — huge, drifting */}
      <div style={{ position: "relative", paddingLeft: 8, paddingTop: 4, zIndex: 2 }}>
        <div className="hud-y2k" style={{ marginBottom: 6 }}>
          HORIZON · XI · DUO · LEDGER · v2.0
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 18, flexWrap: "wrap", cursor: "pointer" }} onClick={onLogo}>
          <h1 className="h-mega h-mega-title" style={{ fontSize: 88, margin: 0, lineHeight: 0.85 }}>ENM</h1>
          <h2 className="h-script h-mega-script" style={{ fontSize: 54, margin: 0, lineHeight: 0.85, paddingBottom: 6 }}>ledger.</h2>
          <div className="chrome-puddle" style={{ width: 56, height: 56, borderRadius: "60% 40% 55% 45% / 50% 60% 40% 50%", marginBottom: 8, animation: "orbFloat 6s ease-in-out infinite" }} />
        </div>
      </div>

      {/* Floating chrome ribbon — players + totals */}
      <div className="chrome-ribbon" style={{ marginTop: -8, display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 4, alignItems: "center", padding: 4, borderRadius: 999,
          background: "linear-gradient(180deg, rgba(255,255,255,0.55), rgba(180,225,250,0.18))",
          border: "1px solid rgba(255,255,255,0.6)",
          boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset, 0 -1px 3px rgba(0,80,140,0.18) inset, 0 6px 14px rgba(0,80,140,0.16)",
          position: "relative", zIndex: 1,
        }}>
          <span className="hud" style={{ color: "#0e4a6a", textShadow: "0 1px 0 rgba(255,255,255,0.6)", padding: "0 10px 0 12px" }}>VIEWING&nbsp;AS</span>
          {PLAYERS.map(p => {
            const on = player === p;
            const blipCls = p === "Jezza" ? "" : "blip-violet";
            const onCls = p === "Jezza" ? "pill-mint" : "pill-violet";
            return (
              <button key={p}
                className={`pill pill-sm ${on ? `${onCls} pill-on` : "pill-ghost"}`}
                onClick={() => setPlayer(p as Player)}
                style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span className={`blip ${blipCls}`} style={{ width: 7, height: 7, opacity: on ? 1 : 0.4, animationPlayState: on ? "running" : "paused" }} />
                {p}
              </button>
            );
          })}
        </div>
        <div onClick={onTotal} style={{ display: "flex", gap: 14, alignItems: "center", cursor: "pointer", flexWrap: "wrap", position: "relative", zIndex: 1 }}>
          <span className="hud" style={{ color: "#0e4a6a", textShadow: "0 1px 0 rgba(255,255,255,0.6)" }}>POOL</span>
          <div style={{ fontFamily: "Quicksand", fontWeight: 700, fontSize: 26, color: "#063a5a", textShadow: "0 1px 0 rgba(255,255,255,0.6)", letterSpacing: -0.5 }}>
            {fmtGil(totalRev)}
          </div>
          <span className="hud" style={{ color: "#0e4a6a", textShadow: "0 1px 0 rgba(255,255,255,0.6)" }}>· EACH</span>
          <div style={{ fontFamily: "Quicksand", fontWeight: 700, fontSize: 22, color: "#0a6d4a", textShadow: "0 1px 0 rgba(255,255,255,0.6)", letterSpacing: -0.5 }}>
            {fmtGil(Math.floor(totalRev / 2))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Empty state — aero set piece ────────────────────────────────────────────

function EmptyAero({ orb = "mint", num = "00", title, sub }: { orb?: string; num?: string; title: string; sub?: string }) {
  return (
    <div style={{ position: "relative", padding: "44px 20px 38px", textAlign: "center", overflow: "hidden" }}>
      <div className={`orb orb-${orb}`} style={{
        width: 78, height: 78, margin: "0 auto 14px",
        animation: "orbFloat 5s ease-in-out infinite",
      }}>
        <div className="orb-num" style={{ fontSize: 22 }}>{num}</div>
      </div>
      <div style={{
        position: "absolute", top: 32, left: "50%", transform: "translateX(-50%)",
        fontFamily: "Orbitron", fontSize: 96, fontWeight: 900,
        color: "rgba(255,255,255,0.06)", letterSpacing: -3, lineHeight: 1, pointerEvents: "none",
      }}>{num}</div>
      <div className="h-script" style={{ fontSize: 22, color: "rgba(255,255,255,0.85)", marginBottom: 4 }}>{title}</div>
      {sub && <div className="hud" style={{ opacity: 0.65 }}>{sub}</div>}
    </div>
  );
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

function Dashboard({ getKS, enmRev, enmRuns, onOpen }: {
  getKS: GetKS;
  enmRev: (id: string) => number;
  enmRuns: (id: string) => number;
  onOpen: (e: ENM) => void;
}) {
  const maxRev = Math.max(...ENM_DATA.map(e => enmRev(e.id)), 1);
  const main = ENM_DATA.filter(e => !e.section);
  const promy = ENM_DATA.filter(e => e.section === "promyvion");

  // The 30-cap spires are occasional runs — the shelf starts collapsed and
  // the user's choice sticks across reloads.
  const [promyOpen, setPromyOpen] = useState(() => localStorage.getItem("promyShelfOpen") === "1");
  const togglePromy = () => setPromyOpen(o => {
    localStorage.setItem("promyShelfOpen", o ? "0" : "1");
    return !o;
  });

  const layouts = [
    { side: "left",  orb: "",       accent: "violet", pull: 0   },
    { side: "right", orb: "mint",   accent: "amber",  pull: -32 },
    { side: "left",  orb: "violet", accent: "rose",   pull: -20 },
    { side: "right", orb: "amber",  accent: "mint",   pull: -28 },
    { side: "left",  orb: "rose",   accent: "violet", pull: -16 },
    { side: "right", orb: "",       accent: "amber",  pull: -24 },
  ];

  // Shelf rows continue the main list's numbering and layout rotation.
  // pullOverride flattens the first shelf row so its negative stagger margin
  // can't ride up over the toggle button.
  const renderRow = (enm: ENM, i: number, pullOverride?: number) => {
    const L = layouts[i % layouts.length];
    const rev = enmRev(enm.id);
    const runs = enmRuns(enm.id);
    const kl = kiLabelFor(getKS, enm);
    const sl = stoneLabelFor(getKS, enm);
    const jEnd = playerCdEnd(getKS, enm.cdGroup, "Jezza");
    const dEnd = playerCdEnd(getKS, enm.cdGroup, "Donpeyote");
    const ready = PLAYERS.every(p => playerCdExpired(getKS, enm.cdGroup, p) && !getKS(enm.cdGroup, p).needsRun);

    return (
      <ENMFlow
        key={enm.id}
        enm={enm} rev={rev} runs={runs} kl={kl} sl={sl}
        jEnd={jEnd} dEnd={dEnd} ready={ready}
        layout={pullOverride !== undefined ? { ...L, pull: pullOverride } : L}
        index={i} maxRev={maxRev}
        onOpen={() => onOpen(enm)}
      />
    );
  };

  return (
    <div style={{ position: "relative" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 28, paddingLeft: 8, flexWrap: "wrap" }}>
        <span className="hud-y2k">▼&nbsp;THE&nbsp;ROSTER</span>
        <span className="h-script" style={{ fontSize: 22, opacity: 0.85 }}>your hunting grounds</span>
      </div>

      {main.map((enm, i) => renderRow(enm, i))}

      <button
        onClick={togglePromy}
        aria-expanded={promyOpen}
        className="pol-nav-item"
        style={{
          width: "100%", background: "transparent", textAlign: "left",
          marginTop: 8, marginBottom: promyOpen ? 24 : 0, padding: "12px 8px",
          display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap",
        }}
      >
        <span className="hud-y2k">{promyOpen ? "▼" : "▶"}&nbsp;PROMYVION&nbsp;SPIRES</span>
        <span className="h-script" style={{ fontSize: 22, opacity: 0.85 }}>the level 30 censer runs</span>
        <span className="tag tag-slate">{promy.length} ENMS · LV.30</span>
      </button>

      {promyOpen && promy.map((enm, i) => renderRow(enm, main.length + i, i === 0 ? 0 : undefined))}
    </div>
  );
}

function ENMFlow({ enm, rev, runs, kl, sl, jEnd, dEnd, ready, layout, index, maxRev, onOpen }: {
  enm: ENM;
  rev: number;
  runs: number;
  kl: string | null;
  sl: string | null;
  jEnd: Date | null;
  dEnd: Date | null;
  ready: boolean;
  layout: { side: string; orb: string; accent: string; pull: number };
  index: number;
  maxRev: number;
  onOpen: () => void;
}) {
  const isLeft = layout.side === "left";
  const ratio = maxRev > 0 ? rev / maxRev : 0;
  const orbSize = Math.round(118 + ratio * 92);
  const accentSize = Math.round(56 + ratio * 28);

  return (
    <div
      onClick={onOpen}
      style={{
        position: "relative",
        marginTop: layout.pull,
        marginBottom: 28,
        minHeight: 200,
        cursor: "pointer",
        animation: `fadeUp .6s ease ${index * 0.07}s both`,
      }}
    >
      <svg width="100%" height="220" viewBox="0 0 920 220" preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, pointerEvents: "none", filter: "blur(0.4px)" }}>
        <defs>
          <radialGradient id={`puddle-${index}`} cx={isLeft ? "30%" : "70%"} cy="50%" r="60%">
            <stop offset="0%"  stopColor="rgba(255,255,255,0.42)" />
            <stop offset="55%" stopColor="rgba(200,240,255,0.16)" />
            <stop offset="100%" stopColor="rgba(180,235,255,0)" />
          </radialGradient>
        </defs>
        {isLeft ? (
          <path d="M 60,110 C 60,40  220,18  430,40  C 620,60  720,30  820,80  C 900,118 870,180 720,196 C 540,214 360,200 220,196 C 100,192 60,180 60,110 Z"
            fill={`url(#puddle-${index})`} />
        ) : (
          <path d="M 100,90 C 220,30  420,40  620,40  C 780,42  860,70  860,120  C 860,180 760,200 580,196 C 380,192 220,210 140,180 C 70,154 60,130 100,90 Z"
            fill={`url(#puddle-${index})`} />
        )}
      </svg>

      {/* Big anchor orb */}
      <div className={`orb ${layout.orb ? `orb-${layout.orb}` : ""}`} style={{
        position: "absolute",
        top: 8 + (200 - orbSize) / 2,
        ...(isLeft ? { left: 4 } : { right: 4 }),
        width: orbSize, height: orbSize,
        animation: `orbFloat ${5 + index * 0.35}s ease-in-out infinite`,
        animationDelay: `${index * 0.25}s`,
        zIndex: 3,
      }}>
        <div className="orb-num" style={{ fontSize: Math.round(28 + ratio * 24) }}>{fmtGilShort(rev)}</div>
        <div className="orb-cap">REVENUE</div>
      </div>

      {/* Small accent orb */}
      <div className={`orb orb-${layout.accent}`} style={{
        position: "absolute",
        top: 8 + (200 - orbSize) / 2 + orbSize - accentSize / 2,
        ...(isLeft ? { left: orbSize - 26 } : { right: orbSize - 26 }),
        width: accentSize, height: accentSize,
        animation: `orbFloat ${4 + index * 0.3}s ease-in-out infinite`,
        animationDelay: `${index * 0.45 + 0.8}s`,
        zIndex: 4,
      }}>
        <div className="orb-num orb-num-sm" style={{ fontSize: Math.round(16 + ratio * 10) }}>{runs}</div>
        <div className="orb-cap" style={{ fontSize: 8 }}>RUNS</div>
      </div>

      {/* Title text */}
      <div style={{
        position: "relative", zIndex: 2,
        paddingTop: 22,
        paddingLeft:  isLeft ? 222 : 14,
        paddingRight: isLeft ? 14  : 222,
        textAlign: isLeft ? "left" : "right",
      }}>
        <div style={{
          position: "absolute",
          top: -8,
          ...(isLeft ? { right: 14 } : { left: 14 }),
          fontFamily: "Orbitron", fontSize: 96, fontWeight: 900, lineHeight: 1,
          letterSpacing: -3,
          color: "rgba(255,255,255,0.10)",
          textShadow: "0 2px 0 rgba(255,255,255,0.10)",
          pointerEvents: "none",
        }}>
          {String(index + 1).padStart(2, "0")}
        </div>

        <div className="hud" style={{ marginBottom: 4 }}>{enm.zone.toUpperCase()} · LV.{enm.level}</div>
        <div className="h-aero" style={{ fontSize: 34, lineHeight: 1.0, marginBottom: 4 }}>{enm.name}</div>
        <div className="h-script" style={{ fontSize: 17, opacity: 0.88, marginBottom: 14 }}>
          🔑 {enm.kiName} · {enm.exp.toLocaleString()}&nbsp;EXP
        </div>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", justifyContent: isLeft ? "flex-start" : "flex-end" }}>
          {ready && <span className="tag tag-mint"><span style={{ width: 6, height: 6, borderRadius: 999, background: "#2aa069", boxShadow: "0 0 8px #6ee0a8" }} />Ready</span>}
          <CooldownTag prefix="J" end={jEnd} />
          <CooldownTag prefix="D" end={dEnd} />
          {sl && <span className="tag tag-slate">🪨 {sl}</span>}
          {kl && <span className="tag tag-violet">🔑 {kl}</span>}
        </div>
      </div>
    </div>
  );
}

// ─── DetailView ──────────────────────────────────────────────────────────────

type PresenceIntent = { kind: "drop" | "sale"; enm_id: string };
type PresenceIntentSetter = Dispatch<SetStateAction<PresenceIntent | null>>;

interface DetailProps {
  enm: ENM;
  drops: Drop[];
  sales: Sale[];
  prices: Record<string, PriceEntry>;
  getKS: GetKS;
  tab: "drops" | "sales" | "ki" | "prices";
  setTab: (t: "drops" | "sales" | "ki" | "prices") => void;
  player: Player;
  setPresenceIntent: PresenceIntentSetter;
  logRunOpen: boolean;
  setLogRunOpen: (b: boolean) => void;
  onBack: () => void;
  rev: number;
  runs: number;
  logRun: ReturnType<typeof useDrops>["logRun"];
  deleteDrop: ReturnType<typeof useDrops>["deleteDrop"];
  logSale: ReturnType<typeof useSales>["logSale"];
  deleteSale: ReturnType<typeof useSales>["deleteSale"];
  toggleStep: ReturnType<typeof useKIState>["toggleStep"];
  spentKI: ReturnType<typeof useKIState>["spentKI"];
  refreshKI: ReturnType<typeof useKIState>["refresh"];
  updatePrice: ReturnType<typeof usePrices>["updatePrice"];
  refreshFromAH: ReturnType<typeof usePrices>["refreshFromAH"];
  refreshing: boolean;
  refreshError: string | null;
  refreshResults: Record<string, { price: number } | { error: string }> | null;
}

function DetailView({
  enm, drops, sales, prices, getKS, tab, setTab, player, setPresenceIntent, logRunOpen, setLogRunOpen, onBack, rev, runs,
  logRun, deleteDrop, logSale, deleteSale, toggleStep, spentKI, refreshKI,
  updatePrice, refreshFromAH, refreshing, refreshError, refreshResults,
}: DetailProps) {
  const ed = drops.filter(d => d.enm_id === enm.id);
  const es = sales.filter(s => s.enm_id === enm.id);
  const eT = es.reduce((s, x) => s + x.price, 0);
  const allI = enm.dropPools.flatMap(p => p.items.map(i => i.name));
  const prI = [...new Set(enm.dropPools.flatMap(p => p.items.map(i => i.sell || i.name)))];
  const kl = kiLabelFor(getKS, enm);
  const sl = stoneLabelFor(getKS, enm);

  const tabs: Array<{ id: "drops" | "sales" | "ki" | "prices"; l: string; ic: string }> = [
    { id: "drops",  l: "Drops",  ic: "💧" },
    { id: "sales",  l: "Sales",  ic: "✦"  },
    { id: "ki",     l: "KI",     ic: "🔑" },
    { id: "prices", l: "Prices", ic: "◈"  },
  ];

  // Match drops to sales for accurate pricing — preserves the original logic
  const dropItemSale = useMemo(() => {
    const drops_asc = [...ed].sort((a, b) => new Date(a.run_date).getTime() - new Date(b.run_date).getTime());
    const sales_asc = [...es].sort((a, b) => new Date(a.sale_date).getTime() - new Date(b.sale_date).getTime());
    const sellName = (n: string) => enm.dropPools.flatMap(p => p.items).find(it => it.name === n)?.sell || n;
    const used = new Set<string>();
    const out: Record<string, number> = {};
    for (const d of drops_asc) {
      const dt = new Date(d.run_date).getTime();
      for (let i = 0; i < d.items.length; i++) {
        const target = sellName(d.items[i]);
        const m = sales_asc.find(s => !used.has(s.id) && s.item_name === target && new Date(s.sale_date).getTime() >= dt);
        if (m) { used.add(m.id); out[`${d.id}:${i}`] = m.price; }
      }
    }
    return out;
  }, [ed, es, enm]);

  const gP = (n: string) => {
    const sa = enm.dropPools.flatMap(p => p.items).find(i => i.name === n)?.sell;
    return prices[sa || n]?.price || 0;
  };

  return (
    <div style={{ animation: "fadeUp .35s ease both" }}>
      <button className="pill pill-ghost pill-sm" onClick={onBack} style={{ marginBottom: 14 }}>← All ENMs</button>

      <div className="pol-bar" style={{ marginBottom: 14, fontSize: 10 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="blip blip-violet" />
          CHANNEL · ENM
        </span>
        <span style={{ opacity: 0.55 }}>│</span>
        <span style={{ opacity: 0.85 }}>{enm.zone.toUpperCase()}</span>
        <span style={{ opacity: 0.55 }}>│</span>
        <span style={{ opacity: 0.85 }}>LV.{enm.level}</span>
        <span style={{ flex: 1 }} />
        <span style={{ opacity: 0.65, fontFamily: "Orbitron", fontSize: 9, letterSpacing: 1.4 }}>ID · {String(ENM_DATA.indexOf(enm) + 1).padStart(2, "0")}</span>
      </div>

      <div style={{ position: "relative", padding: "20px 8px 100px 8px" }}>
        <div className="hud-y2k" style={{ marginBottom: 6 }}>NOW VIEWING · {enm.zone.toUpperCase()}</div>
        <h1 className="h-mega h-mega-title" style={{ fontSize: 64, margin: 0, lineHeight: 0.95, maxWidth: 600 }}>{enm.name}</h1>
        <div className="h-script h-mega-script" style={{ fontSize: 22, marginTop: 6 }}>
          Lv.{enm.level} · 🔑 {enm.kiName} · {enm.exp.toLocaleString()} EXP
        </div>

        {/* Constellation of orbs */}
        <div className="constellation" style={{ position: "absolute", top: 0, right: 0, width: 280, height: 260, pointerEvents: "none" }}>
          <div className="orb orb-mint" style={{ position: "absolute", top: 0, right: 60, width: 110, height: 110, animation: "orbFloat 5.5s ease-in-out infinite", pointerEvents: "auto" }}>
            <div className="orb-num">{fmtGilShort(rev)}</div>
            <div className="orb-cap">REVENUE</div>
          </div>
          <div className="orb" style={{ position: "absolute", top: 90, right: 180, width: 78, height: 78, animation: "orbFloat 4.5s ease-in-out infinite", animationDelay: "0.6s", pointerEvents: "auto" }}>
            <div className="orb-num orb-num-sm">{runs}</div>
            <div className="orb-cap">RUNS</div>
          </div>
          <div className="orb orb-violet" style={{ position: "absolute", top: 130, right: 0, width: 90, height: 90, animation: "orbFloat 6s ease-in-out infinite", animationDelay: "1.1s", pointerEvents: "auto" }}>
            <div className="orb-num orb-num-sm">{runs ? fmtGilShort(Math.floor(rev / runs)) : "—"}</div>
            <div className="orb-cap">AVG/RUN</div>
          </div>
          <div className="orb orb-amber" style={{ position: "absolute", top: 200, right: 110, width: 60, height: 60, animation: "orbFloat 5s ease-in-out infinite", animationDelay: "1.6s", pointerEvents: "auto" }}>
            <div className="orb-num" style={{ fontSize: 18 }}>{fmtGilShort(Math.floor(rev / 2))}</div>
            <div className="orb-cap" style={{ fontSize: 7 }}>EACH</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, marginTop: 26, flexWrap: "wrap", alignItems: "center" }}>
          {sl && <span className="tag tag-slate">🪨 {sl}</span>}
          {kl && <span className="tag tag-violet">🔑 {kl}</span>}
          {PLAYERS.map(p => <CooldownTag key={p} prefix={p[0]} end={playerCdEnd(getKS, enm.cdGroup, p)} />)}
          <button className="pill pill-mint pill-sm" onClick={() => setLogRunOpen(true)}>+ Log Run</button>
        </div>
      </div>

      <div className="detail-grid" style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 18, alignItems: "start" }}>
        <div className="pol-panel detail-rail" style={{ position: "sticky", top: 12 }}>
          <div className="pol-panel-head"><span className="blip" />MENU · 04</div>
          <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 4 }}>
            {tabs.map((t, ti) => (
              <div key={t.id}
                className={`pol-nav-item ${tab === t.id ? "on" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => setTab(t.id)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setTab(t.id); } }}>
                <span className="pol-nav-num">{String(ti + 1).padStart(2, "0")}</span>
                <span style={{ flex: 1 }}>{t.l}</span>
                <span style={{ fontSize: 12, opacity: 0.7 }}>{t.ic}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ minWidth: 0 }}>
          {logRunOpen && (
            <LogRunPanel
              enm={enm}
              runs={runs}
              onClose={() => setLogRunOpen(false)}
              onSave={async (items) => {
                await logRun(enm, items, runs + 1);
                setLogRunOpen(false);
                await refreshKI();
              }}
            />
          )}

          {tab === "drops"  && <DropsTab enm={enm} ed={ed} prices={prices} dropItemSale={dropItemSale} gP={gP} deleteDrop={deleteDrop} />}
          {tab === "sales"  && <SalesTab enm={enm} es={es} eT={eT} prI={prI} setPresenceIntent={setPresenceIntent} logSale={logSale} deleteSale={deleteSale} />}
          {tab === "ki"     && <KITab enm={enm} getKS={getKS} player={player} toggleStep={toggleStep} spentKI={spentKI} />}
          {tab === "prices" && <PricesTab enm={enm} prI={prI} prices={prices} updatePrice={updatePrice} refreshFromAH={refreshFromAH} refreshing={refreshing} refreshError={refreshError} refreshResults={refreshResults} />}
        </div>
      </div>
    </div>
  );
}

// ─── Log Run Panel ───────────────────────────────────────────────────────────

function LogRunPanel({ enm, runs, onClose, onSave }: { enm: ENM; runs: number; onClose: () => void; onSave: (items: string[]) => Promise<void> }) {
  const [sel, setSel] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const tog = (k: string) => setSel(p => p.includes(k) ? p.filter(x => x !== k) : [...p, k]);

  const handleSave = async () => {
    setBusy(true);
    try {
      const items = sel.map(k => k.slice(k.indexOf(":") + 1));
      await onSave(items);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pol-panel scanlines" style={{ marginBottom: 18, animation: "fadeUp .3s ease both" }}>
      <div className="pol-panel-head">
        <span className="blip blip-amber" />LOG RUN · #{String(runs + 1).padStart(3, "0")}
        <span style={{ flex: 1 }} />
        <span style={{ opacity: 0.7, fontSize: 9 }}>TAP ITEMS THAT DROPPED</span>
      </div>
      <div style={{ padding: 18 }}>
        {enm.dropPools.map((pool, pi) => (
          <div key={pi} style={{ marginTop: pi ? 16 : 0 }}>
            <div style={{
              fontFamily: "Orbitron", fontSize: 9, letterSpacing: 1.8, fontWeight: 700,
              color: "rgba(255,255,255,0.6)", marginBottom: 8,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <span className="pol-idx">▸ {String(pi + 1).padStart(2, "0")}</span>
              <span>{pool.label.toUpperCase()} · {pool.type.toUpperCase()}</span>
              <span className="elbow" />
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {pool.items.map((item, ii) => {
                const k = `${pi}:${item.name}`;
                const on = sel.includes(k);
                return (
                  <button key={ii}
                    className={`pill pill-sm ${on ? "pill-mint pill-on" : "pill-ghost"}`}
                    onClick={() => tog(k)}>
                    {on && "✓ "}{item.name}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <div style={{ display: "flex", gap: 8, marginTop: 22, alignItems: "center", flexWrap: "wrap" }}>
          <button className="pill pill-mint" onClick={handleSave} disabled={busy}>{busy ? "Saving…" : "Save Run"}</button>
          <button className="pill pill-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <span style={{ flex: 1 }} />
          <span className="hud" style={{ opacity: 0.7 }}>{sel.length} ITEM{sel.length !== 1 ? "S" : ""} SELECTED</span>
        </div>
      </div>
    </div>
  );
}

// ─── Drops Tab ───────────────────────────────────────────────────────────────

function DropsTab({ enm, ed, prices, dropItemSale, gP, deleteDrop }: {
  enm: ENM;
  ed: Drop[];
  prices: Record<string, PriceEntry>;
  dropItemSale: Record<string, number>;
  gP: (n: string) => number;
  deleteDrop: (id: string) => Promise<void>;
}) {
  const dRate = (n: string) => {
    if (!ed.length) return null;
    const c = ed.reduce((s, d) => s + d.items.filter(x => x === n).length, 0);
    return { c, t: ed.length, r: Math.round(c / ed.length * 1000) / 10 };
  };

  const allEV = enm.dropPools.flatMap(p => p.items.map(i => {
    const ddr = dRate(i.name);
    const dpr = gP(i.name);
    return ddr && dpr ? (ddr.r / 100) * dpr : 0;
  }));
  const maxEV = Math.max(...allEV, 1);

  return (
    <div>
      <div className="pol-panel scanlines" style={{ marginBottom: 22 }}>
        <div className="pol-panel-head">
          <span className="blip" />DROP POOLS · {enm.dropPools.length}
          <span style={{ flex: 1 }} />
          <span style={{ opacity: 0.7, fontSize: 9 }}>NAME · DROP RATE · PRICE</span>
        </div>
        <div style={{ padding: "4px 0" }}>
          {enm.dropPools.map((pool, pi) => (
            <Fragment key={pi}>
              <div style={{
                padding: "10px 18px 6px 18px",
                fontFamily: "Orbitron", fontSize: 9, letterSpacing: 1.8, fontWeight: 700,
                color: "rgba(255,255,255,0.55)", textShadow: "0 1px 0 rgba(0,60,90,0.3)",
                display: "flex", alignItems: "center", gap: 10,
              }}>
                <span className="pol-idx">▸ {String(pi + 1).padStart(2, "0")}</span>
                <span>{pool.label.toUpperCase()}</span>
                <span style={{ opacity: 0.55 }}>· {pool.type.toUpperCase()}</span>
                <span className="elbow" />
              </div>
              {pool.items.map((item, ii) => {
                const dr = dRate(item.name);
                const pr = gP(item.name);
                const pct = dr ? Math.min(100, dr.r) : 0;
                const ev = dr && pr ? (dr.r / 100) * pr : 0;
                const heat = ev / maxEV;
                const fillCls = heat > 0.55 ? "" : heat > 0.20 ? "pol-bar-fill-amber" : "pol-bar-fill-rose";
                return (
                  <div key={ii} className="pol-row">
                    <div className="pol-icon-chrome">{item.name.slice(0, 2).toUpperCase()}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.95)", marginBottom: 5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</div>
                      <div className="pol-bar-track" style={{ width: "60%", maxWidth: 180 }}>
                        {dr && <div className={`pol-bar-fill ${fillCls}`} style={{ right: `${100 - pct}%` }} />}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                      {pr > 0 && <span className="price-mint" style={{ fontSize: 13 }}>{fmtGilShort(pr)}</span>}
                      {dr ? (
                        <span style={{ fontFamily: "Orbitron", fontSize: 9, letterSpacing: 1.2, color: "rgba(255,255,255,0.65)", fontWeight: 700 }}>
                          {dr.c}/{dr.t} · {dr.r}%
                        </span>
                      ) : <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>—</span>}
                    </div>
                  </div>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>

      <div className="pol-panel scanlines">
        <div className="pol-panel-head">
          <span className="blip blip-amber" />RUN HISTORY · {ed.length}
          <span style={{ flex: 1 }} />
          <span style={{ opacity: 0.7, fontSize: 9 }}>NEWEST FIRST</span>
        </div>
        <div className="scroll-aero" style={{ padding: "4px 0", maxHeight: 360 }}>
          {!ed.length ? <EmptyAero orb="" num="00" title="The chest is still sealed" sub="log a run to fill the ledger" />
            : [...ed].sort((a, b) => new Date(b.run_date).getTime() - new Date(a.run_date).getTime()).map(log => {
              let anyEst = false;
              const total = log.items.reduce((s, n, idx) => {
                const matched = dropItemSale[`${log.id}:${idx}`];
                if (matched == null) { anyEst = true; return s + gP(n); }
                return s + matched;
              }, 0);
              return (
                <div key={log.id} className="pol-row">
                  <div className="pol-icon-chrome">#{log.run_number}</div>
                  <div style={{ minWidth: 0 }}>
                    <div className="hud" style={{ marginBottom: 2 }}>{fmtDate(log.run_date)}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.92)" }}>
                      {log.items.length ? log.items.join(", ") : <span style={{ color: "rgba(255,255,255,0.55)", fontStyle: "italic" }}>nothing</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {total > 0 && (
                      <span className="price-mint" title={anyEst ? "Estimated — some items not yet sold" : "Actual — all items sold"} style={{ fontSize: 14 }}>
                        {anyEst ? "~" : ""}{fmtGilShort(total)}
                      </span>
                    )}
                    <button className="pill pill-coral pill-xs" onClick={(e) => { e.stopPropagation(); deleteDrop(log.id); }}>✕</button>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

// ─── Sales Tab ───────────────────────────────────────────────────────────────

function SalesTab({ enm, es, eT, prI, setPresenceIntent, logSale, deleteSale }: {
  enm: ENM;
  es: Sale[];
  eT: number;
  prI: string[];
  setPresenceIntent: PresenceIntentSetter;
  logSale: ReturnType<typeof useSales>["logSale"];
  deleteSale: ReturnType<typeof useSales>["deleteSale"];
}) {
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState("");
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);

  // Drive the App's centralized presenceIntent. Only writes "sale" — never
  // overwrites a "drop" intent set by the App-level Log Run effect, even on
  // tab switch / unmount. The single broadcaster effect in App handles the
  // actual WS frame.
  useEffect(() => {
    if (open) {
      setPresenceIntent({ kind: "sale", enm_id: enm.id });
    } else {
      setPresenceIntent(prev => prev?.kind === "sale" ? null : prev);
    }
    return () => {
      setPresenceIntent(prev => prev?.kind === "sale" ? null : prev);
    };
  }, [open, enm.id, setPresenceIntent]);

  const save = async () => {
    if (!item || !price) return;
    setBusy(true);
    try {
      await logSale(enm.id, item, parseInt(price));
      setItem(""); setPrice(""); setOpen(false);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14, paddingLeft: 4, flexWrap: "wrap" }}>
        <span className="hud-y2k">▼&nbsp;SALES&nbsp;LEDGER</span>
        <span className="price-mint" style={{ fontSize: 20 }}>{fmtGil(eT)}</span>
        <span className="h-script" style={{ fontSize: 16, opacity: 0.8 }}>each gets {fmtGil(Math.floor(eT / 2))}</span>
        <span style={{ flex: 1 }} />
        <button className="pill pill-mint pill-sm" onClick={() => setOpen(o => !o)}>{open ? "× Cancel" : "+ Log Sale"}</button>
      </div>

      {open && (
        <div className="pol-panel scanlines" style={{ marginBottom: 14, animation: "fadeUp .3s ease both" }}>
          <div className="pol-panel-head">
            <span className="blip blip-amber" />NEW SALE
            <span style={{ flex: 1 }} />
            <span style={{ opacity: 0.7, fontSize: 9 }}>ITEM · PRICE</span>
          </div>
          <div style={{ padding: 18, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select className="input-aero" value={item} onChange={e => setItem(e.target.value)} style={{ minWidth: 220 }}>
              <option value="">Select item…</option>
              {prI.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <input className="input-aero" type="number" placeholder="Gil" value={price} onChange={e => setPrice(e.target.value)} style={{ width: 160 }} />
            <button className="pill pill-mint pill-sm" onClick={save} disabled={!item || !price || busy}>{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      )}

      <div className="pol-panel scanlines">
        <div className="pol-panel-head">
          <span className="blip" />INBOX · {es.length} RECEIPTS
          <span style={{ flex: 1 }} />
          <span style={{ opacity: 0.7, fontSize: 9 }}>ITEM · DATE · SELLER · PRICE</span>
        </div>
        <div className="scroll-aero" style={{ padding: "4px 0", maxHeight: 460 }}>
          {!es.length ? <EmptyAero orb="mint" num="0g" title="The vault is quiet" sub="no sales recorded yet" />
            : es.map(sale => {
              const isAuto = sale.source === "cron" || sale.source === "sync";
              return (
                <div key={sale.id} className="pol-row">
                  <div className={`pol-icon-chrome ${isAuto ? "amber" : ""}`}>{isAuto ? "AH" : "✦"}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.94)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sale.item_name}</div>
                    <div className="hud" style={{ marginTop: 2 }}>{fmtDate(sale.sale_date)} · {sale.seller}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ textAlign: "right" }}>
                      <div className="price-mint" style={{ fontSize: 16 }}>{fmtGil(sale.price)}</div>
                      <div style={{ fontFamily: "Orbitron", fontSize: 9, fontWeight: 700, letterSpacing: 1.2, color: "rgba(255,255,255,0.62)", marginTop: 1 }}>
                        EACH · {fmtGilShort(Math.floor(sale.price / 2))}
                      </div>
                    </div>
                    <button className="pill pill-coral pill-xs" onClick={() => deleteSale(sale.id)}>✕</button>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

// ─── KI Tab ──────────────────────────────────────────────────────────────────

function KITab({ enm, getKS, player, toggleStep, spentKI }: {
  enm: ENM;
  getKS: GetKS;
  player: Player;
  toggleStep: ReturnType<typeof useKIState>["toggleStep"];
  spentKI: ReturnType<typeof useKIState>["spentKI"];
}) {
  // Grouped ENMs (Holy Cow / Brothers) share this KI + cooldown — surface
  // that in the header so the either-or is visible from either detail page.
  const siblings = ENM_DATA.filter(e => e.cdGroup === enm.cdGroup && e.id !== enm.id);

  return (
    <div className="pol-panel scanlines">
      <div className="pol-panel-head">
        <span className="blip blip-violet" />KEY ITEM · MEMBER STATUS
        <span style={{ flex: 1 }} />
        <span style={{ opacity: 0.7, fontSize: 9 }}>
          {enm.kiName.toUpperCase()}
          {siblings.length > 0 && ` · SHARED WITH ${siblings.map(s => s.name.toUpperCase()).join(" · ")}`}
        </span>
      </div>
      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 14 }}>
        {PLAYERS.map(p => {
          const ks = getKS(enm.cdGroup, p);
          const allDone = enm.kiSteps.every(s => ks.steps[s.id]);
          const isA = p === player;
          const tl = playerTimeLeft(getKS, enm.cdGroup, p);
          const holding = ks.needsRun;

          return (
            <div key={p} style={{
              position: "relative",
              // Strict concentric inside .pol-panel (18px) with 14px panel
              // padding would be 4px — too flat. 12px stays visibly smaller
              // than parent while keeping the card legible.
              padding: 16, borderRadius: 12,
              background: isA
                ? (holding
                    ? "linear-gradient(180deg, rgba(200,170,255,0.32), rgba(140,110,210,0.20))"
                    : "linear-gradient(180deg, rgba(255,255,255,0.22), rgba(180,225,250,0.10))")
                : "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(180,225,250,0.02))",
              border: isA
                ? (holding ? "1.5px solid rgba(212,168,255,0.7)" : "1.5px solid rgba(255,255,255,0.55)")
                : "1px solid rgba(255,255,255,0.14)",
              boxShadow: isA
                ? "0 1px 0 rgba(255,255,255,0.85) inset, 0 -1px 4px rgba(0,80,140,0.28) inset, 0 12px 28px rgba(0,80,140,0.28), 0 0 0 1px rgba(180,240,255,0.45)"
                : "0 1px 0 rgba(255,255,255,0.25) inset, 0 4px 14px rgba(0,40,90,0.10)",
              opacity: isA ? 1 : 0.72,
              transform: isA ? "none" : "scale(0.985)",
              transition: "all .3s ease",
            }}>
              {isA && <div style={{
                position: "absolute", top: -12, left: 12,
                fontFamily: "Orbitron", fontSize: 9, letterSpacing: 2.4, fontWeight: 700,
                padding: "3px 10px", borderRadius: 999, color: "#063a5a",
                background: "linear-gradient(180deg, #ffffff, #d8eef8 50%, #88b8d0 100%)",
                border: "1px solid rgba(255,255,255,0.85)",
                boxShadow: "0 1px 0 rgba(255,255,255,0.95) inset, 0 -1px 3px rgba(0,80,140,0.3) inset, 0 4px 10px rgba(0,80,140,0.25)",
                textShadow: "0 1px 0 rgba(255,255,255,0.6)",
              }}>● YOU</div>}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="pol-friend-icon" style={{ width: 44, height: 44, fontSize: 18 }}>{p[0]}</div>
                  <div>
                    <div className="h-aero" style={{ fontSize: 22 }}>{p}</div>
                    <div className="hud" style={{ marginTop: 2, display: "flex", alignItems: "center", gap: 6 }}>
                      <span className={`blip ${holding ? "blip-violet" : tl ? "blip-amber" : ""}`} style={{ width: 6, height: 6 }} />
                      {isA ? "YOU · " : ""}{holding ? "HOLDING KI" : tl ? "ON COOLDOWN" : "AVAILABLE"}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  {holding && <span className="tag tag-violet">🔑 Holding KI</span>}
                  <CooldownTag end={playerCdEnd(getKS, enm.cdGroup, p)} />
                  {!holding && playerCdExpired(getKS, enm.cdGroup, p) && !allDone && <span className="tag tag-mint">Available</span>}
                  {isA && !tl && <button className="pill pill-ghost pill-xs" onClick={() => spentKI(enm, p)}>Spent KI</button>}
                </div>
              </div>

              {enm.kiSteps.map((step, si) => {
                const done = !!ks.steps[step.id];
                const isLast = si === enm.kiSteps.length - 1;
                const cdActive = !playerCdExpired(getKS, enm.cdGroup, p);
                // Last step acquires the KI — block it while holding (already
                // have it) or during cooldown (would reset acquiredAt and
                // bypass the timer). Pre-farming earlier steps stays allowed.
                const disabled = !isA || (isLast && (holding || cdActive));
                return (
                  <div key={step.id}
                    className={`ki-step ${done ? "done" : ""} ${disabled ? "disabled" : ""}`}
                    onClick={() => !disabled && toggleStep(enm, step.id, p)}>
                    <div className={`ki-orb-num ${done ? "done" : ""}`}>{done ? "✓" : si + 1}</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: done ? "#caffd9" : "rgba(255,255,255,0.94)" }}>{step.label}</div>
                      <div className="hud" style={{ marginTop: 2 }}>{step.loc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Prices Tab ──────────────────────────────────────────────────────────────

function PricesTab({ enm, prI, prices, updatePrice, refreshFromAH, refreshing, refreshError, refreshResults }: {
  enm: ENM;
  prI: string[];
  prices: Record<string, PriceEntry>;
  updatePrice: ReturnType<typeof usePrices>["updatePrice"];
  refreshFromAH: ReturnType<typeof usePrices>["refreshFromAH"];
  refreshing: boolean;
  refreshError: string | null;
  refreshResults: Record<string, { price: number } | { error: string }> | null;
}) {
  const [eP, setEP] = useState<string | null>(null);
  const [eV, setEV] = useState("");

  const uP = async (n: string, v: string) => {
    if (!v) return;
    await updatePrice(n, parseInt(v));
    setEP(null); setEV("");
  };

  // Compute per-item "pool max" so bars are color-coded by relative value
  const maxPrice = Math.max(...prI.map(n => prices[n]?.price || 0), 1);

  return (
    <div className="pol-panel scanlines">
      <div className="pol-panel-head">
        <span className="blip" />PRICE CHECK · AH SYNC
        <span style={{ flex: 1 }} />
        <button className="pill pill-aqua pill-xs" onClick={() => refreshFromAH(prI)} disabled={refreshing}>
          {refreshing ? "··· CHECKING" : "↻ REFRESH"}
        </button>
      </div>

      {refreshError && (
        <div style={{ padding: "10px 18px", borderBottom: "1px solid rgba(255,255,255,0.12)", color: "#ffb0a8", fontSize: 11, fontWeight: 700, fontFamily: "Inter", letterSpacing: 1, textTransform: "uppercase" }}>
          AH refresh failed: {refreshError}
        </div>
      )}

      <div style={{ padding: "4px 0" }}>
        {prI.map((name, i) => {
          const p = prices[name];
          const editing = eP === name;
          const rr = refreshResults?.[name];
          const rErr = rr && "error" in rr ? rr.error : null;
          const value = p?.price || 0;
          const pct = maxPrice > 0 ? (value / maxPrice) * 100 : 0;
          const heat = value / maxPrice;
          const fillCls = heat > 0.55 ? "" : heat > 0.20 ? "pol-bar-fill-amber" : "pol-bar-fill-rose";

          return (
            <div key={name} className="pol-row">
              <div className="pol-icon-chrome">{String(i + 1).padStart(2, "0")}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.94)", marginBottom: 5 }}>{name}</div>
                {value > 0 ? (
                  <div className="pol-bar-track" style={{ width: "70%", maxWidth: 220 }}>
                    <div className={`pol-bar-fill ${fillCls}`} style={{ right: `${100 - pct}%` }} />
                  </div>
                ) : null}
                {p?.updated && <div className="hud" style={{ marginTop: 5 }}>{fmtDate(p.updated)}</div>}
                {rErr && <div style={{ fontSize: 10, color: "#ffb0a8", fontWeight: 700, marginTop: 3 }}>AH: {rErr}</div>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {editing ? (
                  <>
                    <input className="input-aero" type="number" value={eV} onChange={e => setEV(e.target.value)} placeholder="Gil" style={{ width: 100, fontSize: 12, padding: "5px 12px" }} autoFocus onKeyDown={e => e.key === "Enter" && uP(name, eV)} />
                    <button className="pill pill-mint pill-xs" onClick={() => uP(name, eV)}>✓</button>
                    <button className="pill pill-ghost pill-xs" onClick={() => { setEP(null); setEV(""); }}>✕</button>
                  </>
                ) : (
                  <>
                    <span className="price-mint" style={{ fontSize: 16, opacity: value ? 1 : 0.25 }}>{value ? fmtGil(value) : "—"}</span>
                    <button className="pill pill-ghost pill-xs" onClick={() => { setEP(name); setEV(value ? value.toString() : ""); }}>Edit</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── All Sales ───────────────────────────────────────────────────────────────

function AllSales({ sales, totalRev, onBack, deleteSale, syncFromAH, syncing, syncError, syncResult }: {
  sales: Sale[];
  totalRev: number;
  onBack: () => void;
  deleteSale: ReturnType<typeof useSales>["deleteSale"];
  syncFromAH: ReturnType<typeof useSales>["syncFromAH"];
  syncing: boolean;
  syncError: string | null;
  syncResult: { imported: number; candidates: number; items_checked?: number; ah_rows_new?: number } | null;
}) {
  const sorted = useMemo(() =>
    [...sales].sort((a, b) => new Date(b.sale_date).getTime() - new Date(a.sale_date).getTime())
  , [sales]);

  return (
    <div style={{ animation: "fadeUp .35s ease both" }}>
      <button className="pill pill-ghost pill-sm" onClick={onBack} style={{ marginBottom: 14 }}>← All ENMs</button>

      <div className="pol-bar" style={{ marginBottom: 14, fontSize: 10 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="blip" />CHANNEL · ALL SALES
        </span>
        <span style={{ opacity: 0.55 }}>│</span>
        <span style={{ opacity: 0.85 }}>{sales.length} ENTRIES</span>
        <span style={{ flex: 1 }} />
        <span style={{ opacity: 0.65, fontFamily: "Orbitron", fontSize: 9, letterSpacing: 1.4 }}>SHARED LEDGER · 2 MEMBERS</span>
      </div>

      <div style={{ position: "relative", padding: "20px 8px 80px 8px" }}>
        <div className="hud-y2k" style={{ marginBottom: 6 }}>EVERY DROP, EVERY GIL</div>
        <h1 className="h-mega h-mega-title" style={{ fontSize: 64, margin: 0, lineHeight: 0.95 }}>All Sales</h1>
        <div className="h-script h-mega-script" style={{ fontSize: 22, marginTop: 6 }}>
          {sales.length} entries · the receipts
        </div>

        <div className="constellation" style={{ position: "absolute", top: -10, right: 0, width: 280, height: 220, pointerEvents: "none" }}>
          <div className="orb orb-mint" style={{ position: "absolute", top: 0, right: 60, width: 130, height: 130, animation: "orbFloat 6s ease-in-out infinite", pointerEvents: "auto" }}>
            <div className="orb-num" style={{ fontSize: 30 }}>{fmtGilShort(totalRev)}</div>
            <div className="orb-cap">POOL TOTAL</div>
          </div>
          <div className="orb" style={{ position: "absolute", top: 110, right: 0, width: 88, height: 88, animation: "orbFloat 5s ease-in-out infinite", animationDelay: "0.7s", pointerEvents: "auto" }}>
            <div className="orb-num orb-num-sm" style={{ fontSize: 20 }}>{fmtGilShort(Math.floor(totalRev / 2))}</div>
            <div className="orb-cap">EACH</div>
          </div>
          <div className="orb orb-amber" style={{ position: "absolute", top: 90, right: 156, width: 56, height: 56, animation: "orbFloat 5.5s ease-in-out infinite", animationDelay: "1.3s", pointerEvents: "auto" }}>
            <div className="orb-num" style={{ fontSize: 18 }}>{sales.length}</div>
            <div className="orb-cap" style={{ fontSize: 7 }}>SALES</div>
          </div>
        </div>
      </div>

      <div className="detail-grid" style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 18, alignItems: "start" }}>
        <div className="pol-panel detail-rail" style={{ position: "sticky", top: 12 }}>
          <div className="pol-panel-head"><span className="blip" />MEMBERS · 02</div>
          <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 6 }}>
            {PLAYERS.map(p => (
              <div key={p} className="pol-friend on">
                <div className="pol-friend-icon">{p[0]}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "Quicksand", fontWeight: 700, fontSize: 13, color: "#fff", textShadow: "0 1px 0 rgba(0,40,80,0.3)" }}>{p}</div>
                  <div style={{ fontFamily: "Orbitron", fontSize: 9, letterSpacing: 1.2, color: "rgba(255,255,255,0.75)", fontWeight: 700, marginTop: 1 }}>
                    {fmtGilShort(Math.floor(totalRev / 2))} GIL
                  </div>
                </div>
                <span className="blip" style={{ width: 7, height: 7 }} />
              </div>
            ))}
          </div>
          <div style={{ padding: "10px 14px 14px", borderTop: "1px solid rgba(255,255,255,0.18)" }}>
            <button className="pill pill-aqua pill-sm" onClick={() => syncFromAH()} disabled={syncing} style={{ width: "100%" }}>
              {syncing ? "··· SYNCING" : "↻ Sync from AH"}
            </button>
            {syncError && <div style={{ fontSize: 10, color: "#ffb0a8", fontWeight: 700, marginTop: 8, fontFamily: "Inter", letterSpacing: 0.6 }}>Sync failed: {syncError}</div>}
            {syncResult && !syncError && (
              <div className="hud" style={{ marginTop: 8, lineHeight: 1.5 }}>
                {syncResult.items_checked != null && <>Checked {syncResult.items_checked}, {syncResult.ah_rows_new ?? 0} new<br /></>}
                {syncResult.imported > 0 ? `Imported ${syncResult.imported}` : "Up to date"}
              </div>
            )}
          </div>
        </div>

        <div className="pol-panel scanlines" style={{ minWidth: 0 }}>
          <div className="pol-panel-head">
            <span className="blip blip-amber" />INBOX · {sales.length} RECEIPTS
            <span style={{ flex: 1 }} />
            <span style={{ opacity: 0.7, fontSize: 9 }}>NEWEST FIRST</span>
          </div>
          <div className="scroll-aero" style={{ padding: "4px 0", maxHeight: 720 }}>
            {!sorted.length ? <EmptyAero orb="mint" num="0g" title="No receipts yet" sub="sync from AH or log a sale to begin" />
              : sorted.map(sale => {
                const enm = ENM_DATA.find(e => e.id === sale.enm_id);
                const isAuto = sale.source === "cron" || sale.source === "sync";
                return (
                  <div key={sale.id} className="pol-row">
                    <div className={`pol-icon-chrome ${isAuto ? "amber" : ""}`}>{isAuto ? "AH" : "✦"}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.94)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sale.item_name}</div>
                      <div className="hud" style={{ marginTop: 2 }}>{enm?.name || sale.enm_id} · {fmtDate(sale.sale_date)} · {sale.seller}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ textAlign: "right" }}>
                        <div className="price-mint" style={{ fontSize: 16 }}>{fmtGil(sale.price)}</div>
                        <div style={{ fontFamily: "Orbitron", fontSize: 9, fontWeight: 700, letterSpacing: 1.2, color: "rgba(255,255,255,0.62)", marginTop: 1 }}>
                          EACH · {fmtGilShort(Math.floor(sale.price / 2))}
                        </div>
                      </div>
                      <button className="pill pill-coral pill-xs" onClick={() => deleteSale(sale.id)}>✕</button>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}
